import PropTypes from 'prop-types'
import { useState, useEffect } from 'react'
import TimelineStep from './TimelineStep'
import projectApprovalService from '@services/projectApprovalService'
import './ApprovalTimeline.css'

/**
 * ApprovalTimeline Component
 *
 * Displays the project approval workflow timeline with current status
 *
 * @param {number} projectId - The project ID
 * @param {string} approvalStatus - Current approval status
 * @param {React.ReactNode} children - Action buttons/content to render inside the timeline
 */
const ApprovalTimeline = ({ projectId, approvalStatus, children }) => {
  const [timeline, setTimeline] = useState([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    fetchTimeline()
  }, [projectId, approvalStatus])

  const fetchTimeline = async () => {
    try {
      setLoading(true)
      const response = await projectApprovalService.getTimeline(projectId)
      setTimeline(response.timeline || [])
    } catch (error) {
      console.error('Failed to fetch timeline:', error)
    } finally {
      setLoading(false)
    }
  }

  // Check if executive review is needed (based on timeline data or status)
  const hasExecutiveReview =
    approvalStatus === 'pending_executive_review' ||
    approvalStatus === 'executive_accepted' ||
    timeline.some(entry => entry.stage === 'executive_reviewed')

  // Define timeline stages
  const stages = [
    {
      key: 'created',
      label: 'Project Created',
      icon: '📝',
      description: 'Project and checklists created',
    },
    {
      key: 'submitted',
      label: 'Submitted for Review',
      icon: '📤',
      description: 'Sent to Project Manager',
    },
    {
      key: 'pm_reviewed',
      label: 'PM Review',
      icon: '👤',
      description: 'Project Manager approval',
    },
    {
      key: 'executive_reviewed',
      label: 'Executive Review',
      icon: '👔',
      description: 'Executive decision on excused items',
      conditional: hasExecutiveReview,
    },
    {
      key: 'activated',
      label: 'Active Project',
      icon: '✅',
      description: 'Ready for task assignment',
    },
  ]

  // Filter out conditional stages
  const visibleStages = stages.filter((stage) => stage.conditional !== false)

  // Determine status for each stage based on timeline data and approval_status
  const getStageStatus = (stageKey) => {
    const timelineEntry = timeline.find((entry) => entry.stage === stageKey)

    if (timelineEntry) {
      return timelineEntry.status === 'completed' ? 'completed' : 'active'
    }

    // Derive status from approval_status
    const stageIndex = visibleStages.findIndex((s) => s.key === stageKey)
    const currentStageIndex = getCurrentStageIndex()

    if (stageIndex < currentStageIndex) {
      return 'completed'
    } else if (stageIndex === currentStageIndex) {
      return 'active'
    } else {
      return 'pending'
    }
  }

  const getCurrentStageIndex = () => {
    switch (approvalStatus) {
      case 'draft':
        return 0
      case 'pending_pm_review':
        return 2
      case 'pm_accepted':
        return 3
      case 'pending_executive_review':
        return 3
      case 'executive_accepted':
      case 'active':
        return visibleStages.length - 1
      default:
        return 0
    }
  }

  if (loading) {
    return (
      <div className="approval-timeline">
        <div className="approval-timeline__loading">Loading timeline...</div>
      </div>
    )
  }

  return (
    <div className="approval-timeline">
      <div className="approval-timeline__header">
        <h3>Approval Timeline</h3>
        <p className="approval-timeline__subtitle">Track your project approval progress</p>
      </div>

      <div className="approval-timeline__steps">
        {visibleStages.map((stage, index) => {
          const status = getStageStatus(stage.key)
          const timelineEntry = timeline.find((entry) => entry.stage === stage.key)

          return (
            <TimelineStep
              key={stage.key}
              stage={stage}
              status={status}
              isLast={index === visibleStages.length - 1}
              timelineData={timelineEntry}
            />
          )
        })}
      </div>

      {children && (
        <div className="approval-timeline__actions">
          {children}
        </div>
      )}
    </div>
  )
}

ApprovalTimeline.propTypes = {
  projectId: PropTypes.number.isRequired,
  approvalStatus: PropTypes.string.isRequired,
  children: PropTypes.node,
}

export default ApprovalTimeline
